// Pillar page — long-form topical hub that concentrates PageRank on
// /aula-gratis via curated internal links to blog posts + programmatic pages.
//
// Route: /:slug where slug is one of PILLAR_KEYS (see /lib/pillarPages.js).
// Emits BreadcrumbList + Article + FAQPage + WebPage(Speakable) JSON-LD.

import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND } from "@/lib/config";
import { formatBlogDate } from "@/lib/formatDate";
import { useCanonical } from "@/lib/useCanonical";
import { AUTHOR, personRef } from "@/lib/authorSchema";
import { getPillar } from "@/lib/pillarPages";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import { ArrowRight, ArrowLeft, BookOpen } from "lucide-react";

const UI = {
  pt: {
    back: "Voltar",
    tldrLabel: "Resposta rápida",
    tocTitle: "Nesta página",
    faqTitle: "Perguntas frequentes",
    aboutAuthor: "Sobre o autor",
    ctaTitle: "Pronto pra treinar isso com um nativo?",
    ctaBody:
      "Aula grátis de 45-60 min com Prof Lionel, nativo de Paris. Sem cartão, sem compromisso.",
    ctaBtn: "Quero minha aula grátis",
    testBtn: "Fazer o teste de nível (5 min)",
    updatedLabel: "Atualizado em",
    readMore: "Ler",
  },
  en: {
    back: "Back",
    tldrLabel: "Quick answer",
    tocTitle: "On this page",
    faqTitle: "Frequently asked questions",
    aboutAuthor: "About the author",
    ctaTitle: "Ready to drill this with a native?",
    ctaBody: "Free 45-60 min class with Prof Lionel, native Parisian. No card, no commitment.",
    ctaBtn: "Get my free class",
    testBtn: "Take the level test (5 min)",
    updatedLabel: "Updated on",
    readMore: "Read",
  },
  fr: {
    back: "Retour",
    tldrLabel: "Réponse rapide",
    tocTitle: "Sur cette page",
    faqTitle: "Questions fréquentes",
    aboutAuthor: "À propos de l'auteur",
    ctaTitle: "Prêt à t'entraîner avec un natif ?",
    ctaBody: "Cours offert de 45-60 min avec Prof Lionel, natif de Paris. Sans CB, sans engagement.",
    ctaBtn: "Je veux mon cours offert",
    testBtn: "Faire le test de niveau (5 min)",
    updatedLabel: "Mis à jour le",
    readMore: "Lire",
  },
};

export default function PillarPage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const ui = UI[lang] || UI.pt;
  const pillar = getPillar(slug);
  useCanonical(pillar ? `/${slug}` : "/");

  useEffect(() => {
    if (!pillar) navigate("/");
  }, [pillar, navigate]);

  useEffect(() => {
    if (!pillar) return;
    const url = `https://npdlbr.com.br/${slug}`;
    const hero = pillar.hero[lang] || pillar.hero.pt;
    const tldr = pillar.tldr[lang] || pillar.tldr.pt;
    const faq = pillar.faq[lang] || pillar.faq.pt || [];

    const setMeta = (selector, attr, value, content) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      } else if (!el.dataset.original) {
        el.dataset.original = el.getAttribute("content") || "";
      }
      el.setAttribute("content", content);
      return { el, created };
    };
    const handles = [
      setMeta('meta[name="description"]', "name", "description", tldr.slice(0, 300)),
      setMeta('meta[property="og:title"]', "property", "og:title", hero.title),
      setMeta('meta[property="og:description"]', "property", "og:description", tldr.slice(0, 300)),
      setMeta('meta[property="og:url"]', "property", "og:url", url),
      setMeta('meta[property="og:type"]', "property", "og:type", "article"),
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", hero.title),
      setMeta('meta[name="twitter:description"]', "name", "twitter:description", tldr.slice(0, 300)),
    ];

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: "https://npdlbr.com.br/" },
        { "@type": "ListItem", position: 2, name: hero.title, item: url },
      ],
    };
    const article = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: hero.title,
      description: tldr,
      author: personRef(),
      publisher: {
        "@type": "Organization",
        name: "Na Ponta Da Língua Brasil",
        logo: {
          "@type": "ImageObject",
          url: "https://customer-assets.emergentagent.com/job_french-for-all-1/artifacts/bv8f2u2u_Capture%20d%E2%80%99e%CC%81cran%202026-06-19%20a%CC%80%2016.45.23.png",
        },
      },
      datePublished: pillar.updated,
      dateModified: pillar.updated,
      inLanguage: lang,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".pillar-tldr", ".pillar-heading"],
      },
    };
    const faqPage = faq.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

    const emit = (obj, tag) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.dynamic = tag;
      el.textContent = JSON.stringify(obj);
      document.head.appendChild(el);
      return el;
    };
    const els = [emit(breadcrumb, "pillar-breadcrumb"), emit(article, "pillar-article")];
    if (faqPage) els.push(emit(faqPage, "pillar-faq"));

    const prevTitle = document.title;
    document.title = `${hero.title} · Prof Lionel`;

    return () => {
      els.forEach((el) => el.remove());
      handles.forEach(({ el, created }) => {
        if (created) el.remove();
        else if (el.dataset.original !== undefined) {
          el.setAttribute("content", el.dataset.original);
          delete el.dataset.original;
        }
      });
      document.title = prevTitle;
    };
  }, [pillar, slug, lang]);

  if (!pillar) return null;

  const hero = pillar.hero[lang] || pillar.hero.pt;
  const tldr = pillar.tldr[lang] || pillar.tldr.pt;
  const sections = pillar.sections[lang] || pillar.sections.pt;
  const faq = pillar.faq[lang] || pillar.faq.pt || [];

  return (
    <div data-testid="pillar-page" className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a]">
      <header className="border-b border-[#e5e3db]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#1e2a5a] font-display italic">
              L
            </span>
            <span className="font-display text-sm sm:text-base">{BRAND.name}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/" className="inline-flex items-center gap-1 text-xs sm:text-sm link-underline">
              <ArrowLeft className="h-3.5 w-3.5" /> {ui.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10 sm:py-16">
        <p className="text-xs uppercase tracking-[0.22em]" style={{ color: pillar.color }}>
          {hero.eyebrow}
        </p>
        <h1 className="pillar-heading font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          {hero.title}
        </h1>
        <p className="mt-5 text-lg sm:text-xl text-[#5c5c5c] leading-relaxed">
          {hero.subtitle}
        </p>

        {/* TLDR */}
        <aside
          className="pillar-tldr mt-8 rounded-xl border-l-4 bg-[#f0efea] px-5 py-4 sm:px-6 sm:py-5"
          style={{ borderColor: pillar.color }}
          data-testid="pillar-tldr"
        >
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.22em] font-medium" style={{ color: pillar.color }}>
            {ui.tldrLabel}
          </p>
          <p className="mt-2 text-base sm:text-lg leading-relaxed text-[#1e2a5a]">
            {tldr}
          </p>
        </aside>

        {/* TOC */}
        <nav className="mt-12 rounded-xl border border-[#e5e3db] bg-white p-5 sm:p-6" aria-label={ui.tocTitle}>
          <p className="text-xs uppercase tracking-[0.22em] text-[#5c5c5c] flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5" />
            {ui.tocTitle}
          </p>
          <ol className="mt-3 space-y-1.5 text-sm sm:text-base list-none">
            {sections.map((s, i) => (
              <li key={i}>
                <a
                  href={`#section-${i}`}
                  className="text-[#1e2a5a] hover:text-[#d9381e] transition-colors"
                >
                  {i + 1}. {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
        {sections.map((s, i) => (
          <section
            key={i}
            id={`section-${i}`}
            className="mt-14 scroll-mt-20"
            data-testid={`pillar-section-${i}`}
          >
            <h2 className="pillar-heading font-display text-2xl sm:text-3xl tracking-tight">
              {s.title}
            </h2>
            <p className="mt-4 text-base sm:text-lg leading-[1.75] text-[#3a3a3a]">
              {s.body}
            </p>
            <ul className="mt-6 space-y-2">
              {s.links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="group inline-flex items-start gap-2 text-base text-[#1e2a5a] hover:text-[#d9381e] transition-colors"
                  >
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" style={{ color: pillar.color }} />
                    <span className="underline decoration-transparent group-hover:decoration-[#d9381e] underline-offset-4 transition-all">
                      {l.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="mt-16" data-testid="pillar-faq">
            <h2 className="pillar-heading font-display text-2xl sm:text-3xl tracking-tight">
              {ui.faqTitle}
            </h2>
            <div className="mt-6 divide-y divide-[#e5e3db] border-t border-b border-[#e5e3db]">
              {faq.map((f) => (
                <details key={f.q} className="group py-4">
                  <summary className="flex cursor-pointer items-start justify-between gap-4 font-display text-lg text-[#1e2a5a] hover:text-[#d9381e] transition-colors">
                    <span>{f.q}</span>
                    <span className="mt-1 text-2xl leading-none text-[#d9381e] group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 pr-8 text-base leading-relaxed text-[#3a3a3a]">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Author card */}
        <section
          className="mt-16 rounded-2xl border border-[#e5e3db] bg-white p-6 sm:p-8 flex gap-5 items-start"
          data-testid="pillar-author-card"
        >
          <img
            src={AUTHOR.image}
            alt={`${AUTHOR.name} — ${AUTHOR.jobTitle}`}
            width="80"
            height="80"
            loading="lazy"
            className="w-20 h-20 rounded-full object-cover shrink-0 border border-[#e5e3db]"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#d9381e]">
              {ui.aboutAuthor}
            </p>
            <Link
              to="/prof-lionel"
              className="mt-1 inline-block font-display text-xl sm:text-2xl text-[#1e2a5a] hover:text-[#d9381e] transition-colors"
              data-testid="pillar-author-link"
            >
              {AUTHOR.name}
            </Link>
            <p className="mt-1 text-sm text-[#5c5c5c]">{AUTHOR.jobTitle}</p>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#3a3a3a]">
              {AUTHOR.description}
            </p>
          </div>
        </section>

        {/* CTA */}
        <aside
          className="mt-20 rounded-2xl text-[#f4f1ea] p-8 sm:p-12"
          style={{ background: pillar.color }}
          data-testid="pillar-cta"
        >
          <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight">
            {ui.ctaTitle}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#f4f1ea]/85">{ui.ctaBody}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/aula-gratis"
              className="group inline-flex items-center gap-2 rounded-full bg-[#f4f1ea] px-6 py-3 text-sm font-medium text-[#1e2a5a] transition-colors hover:bg-[#d9381e] hover:text-[#f4f1ea]"
              data-testid="pillar-cta-book"
            >
              {ui.ctaBtn}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/teste-de-nivel"
              className="inline-flex items-center gap-2 rounded-full border border-[#f4f1ea]/40 px-6 py-3 text-sm font-medium text-[#f4f1ea] transition-colors hover:border-[#f4f1ea] hover:bg-[#f4f1ea]/10"
              data-testid="pillar-cta-test"
            >
              {ui.testBtn}
            </Link>
          </div>
        </aside>

        <p className="mt-16 text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
          {ui.updatedLabel}{" "}
          <time dateTime={pillar.updated}>{formatBlogDate(pillar.updated, lang)}</time>
        </p>
      </main>
    </div>
  );
}
